"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RoomService = void 0;
const api_1 = require("../exports/api");
const config_1 = require("../config");
const constant_1 = require("../config/constant");
const dayjs_1 = __importDefault(require("dayjs"));
const buffer_storage_1 = require("../engine/buffer_storage");
const i18n_1 = require("../exports/i18n");
const io_1 = require("../io");
const lodash_1 = require("lodash");
const player_1 = require("../modules/shared/player");
require("socket.io-client");
const store_1 = require("../exports/store");
const actions_1 = require("../exports/store/actions");
const enhance_error_1 = require("../sync/enhance_error");
const types_1 = require("../types");
const utils_1 = require("../utils");
const socket_guard_1 = require("../utils/socket_guard");
const types_2 = require("./types");
// The maximum number of data retransmission actions is online, beyond this value, no timeout retry operation will be performed
const MAX_RETRY_LENGTH = 5000;
const VIKA_OP_BACKUP = 'VIKA_OP_BACKUP';
const MAX_RETRY_COUNT = 1;
/**
 * Room module, responsible for data synchronization in the collaborative room, retry, sending and receiving messages
 * It is not coupled with the resource entity, and the operation of the data entity is handled by CollaEngine
 * 1 Room can manage multiple CollaEngines
 */
class RoomService {
    constructor(roomId, socket, isConnect, store, collaEngineMap, event, fetchResource, lsStore, localForage, retryCount = Number.MAX_VALUE, justReConnected) {
        this.roomId = roomId;
        this.socket = socket;
        this.isConnect = isConnect;
        this.store = store;
        this.fetchResource = fetchResource;
        this.lsStore = lsStore;
        this.localForage = localForage;
        this.retryCount = retryCount;
        this.justReConnected = justReConnected;
        this.collaEngineMap = new Map();
        this.io = new io_1.IO(this.roomId, this.socket);
        this.connected = false;
        /**
         * Send user-generated changeset
         */
        this.sendUserChanges = (changesets, runStatus) => {
            console.log('Submission data: ', changesets, changesets.length);
            // mark the send queue waiting to return
            this.event.setRoomIOClear(false);
            this.event.setRoomLastSendTime();
            if (!this.connected) {
                console.error('room has been destroy,can\'t send anything');
                return;
            }
            // Load after 500ms, to prevent the icon from flashing when the network is fast
            const collaEngine = this.collaEngineMap.get(this.roomId);
            const resourceType = collaEngine === null || collaEngine === void 0 ? void 0 : collaEngine.resourceType;
            const timer = setTimeout(() => {
                this.store.dispatch((0, actions_1.changeResourceSyncingStatus)(this.roomId, resourceType, true));
            }, 500);
            if (runStatus)
                this.store.dispatch((0, actions_1.setMirrorSaveStatus)(this.roomId, runStatus));
            else
                this.store.dispatch((0, actions_1.setMirrorSaveStatus)(this.roomId, config_1.StatusCode.SAVE_RUNING));
            const state = this.store.getState();
            const shareId = state.pageParams.shareId;
            return this.io.request({
                type: types_2.SyncRequestTypes.CLIENT_ROOM_CHANGE,
                roomId: this.roomId,
                changesets,
                shareId,
            }).then((data) => {
                clearTimeout(timer);
                if (data.success && data.data) {
                    this.event.setRoomIOClear(true);
                    this.retryCount = Number.MAX_VALUE;
                    this.store.dispatch((0, actions_1.setMirrorSaveStatus)(this.roomId, config_1.StatusCode.STATUS_OK));
                    this.store.dispatch((0, actions_1.changeResourceSyncingStatus)(this.roomId, resourceType, false));
                    return this.handleAcceptCommit(data.data.changesets);
                }
                else if (data.code === types_2.OtErrorCode.MSG_ID_DUPLICATE) { //重复消息不算错误,没有清理啥东西
                    this.event.setRoomIOClear(true);
                    this.retryCount = Number.MAX_VALUE;
                    console.log('---------MSG_ID_DUPLICATE', data.message);
                    this.store.dispatch((0, actions_1.setMirrorSaveStatus)(this.roomId, config_1.StatusCode.STATUS_OK));
                    this.store.dispatch((0, actions_1.changeResourceSyncingStatus)(this.roomId, resourceType, false));
                    this.nextSend();
                    return;
                }
                // Unsuccessful requests, divert traffic to the following catch
                return Promise.reject(data);
            }).catch((e) => __awaiter(this, void 0, void 0, function* () {
                this.store.dispatch((0, actions_1.setMirrorSaveStatus)(this.roomId, config_1.StatusCode.SAVE_ERROR));
                this.store.dispatch((0, actions_1.changeResourceSyncingStatus)(this.roomId, resourceType, false));
                clearTimeout(timer);
                let errMsg = e;
                console.log('---------handleRejectCommit', errMsg);
                if (!('success' in errMsg)) {
                    errMsg = {
                        success: false,
                        code: 0,
                        message: '保存出错，请刷新重试' //t(Strings.exception_network_exception),
                    };
                }
                if (this.retryCount >= MAX_RETRY_COUNT && this.retryCount != Number.MAX_VALUE) { // two more start clear pengdingchangeset
                    this.event.setRoomIOClear(true);
                    this.retryCount = Number.MAX_VALUE;
                    yield this.handleRejectCommit(errMsg, true); //不挣扎了，承认失败
                }
                else {
                    if (this.retryCount == Number.MAX_VALUE)
                        this.retryCount = 0; //定时15秒后强制发送，后续操作会卡在这里
                    yield this.handleRejectCommit(errMsg, false); //提醒的不是很明显
                }
                return Promise.reject();
            }));
        };
        /**
         * @description for the next round of sending
         * @private
         * When there are currently no queued requests, you can start sending directly
         * Otherwise, after waiting for the current request to end, opBuffer will be sent automatically
         * Called immediately when triggered for the first time, and will be called again after the interval expires
         */
        this.nextSend = (0, lodash_1.throttle)(() => {
            if (!this.event.getRoomIOClear()) {
                console.error('throttledNextSend pending reject');
                return;
            }
            this.justReConnected = false;
            this.retryCount = Number.MAX_VALUE;
            console.error('next send start ');
            const nextChangesets = [];
            for (const resourceId of this.collaEngineMap.keys()) {
                const collaEngine = this.collaEngineMap.get(resourceId);
                const cs = collaEngine.getNextChangeset();
                if (cs) {
                    nextChangesets.push(cs);
                }
            }
            if (nextChangesets.length) {
                this.event.setRoomLastSendTime();
                void this.sendUserChanges(nextChangesets);
            }
        }, 500);
        /**
         * Handle messages actively pushed by io
         */
        this.bindSocketMessage = () => {
            if (!this.io.socket) {
                throw new Error((0, i18n_1.t)(i18n_1.Strings.error_please_bind_message_after_connected));
            }
            this.io.offAll();
            this.io.on(types_2.BroadcastTypes.SERVER_ROOM_CHANGE, (data) => {
                void this.handleNewChanges(data);
            });
            this.io.on(types_2.BroadcastTypes.ENGAGEMENT_CURSOR, (data) => {
                this.handleCursor(data);
            });
            this.io.on(types_2.BroadcastTypes.ACTIVATE_COLLABORATORS, data => {
                this.handleActiveCollaborators(data);
            });
            this.io.on(types_2.BroadcastTypes.DEACTIVATE_COLLABORATOR, data => {
                this.handleDeactivateCollaborator(data);
            });
            this.io.on(types_2.BroadcastTypes.NODE_SHARE_DISABLED, data => {
                this.handleNodeShareDisabled(data);
            });
            this.io.on(types_2.BroadcastTypes.FIELD_PERMISSION_ENABLE, data => {
                this.handleFieldPermissionEnabled(data);
            });
            this.io.on(types_2.BroadcastTypes.FIELD_PERMISSION_CHANGE, data => {
                this.handleFieldPermissionChange(data);
            });
            this.io.on(types_2.BroadcastTypes.FIELD_PERMISSION_DISABLE, data => {
                this.handleFieldPermissionDisabled(data);
            });
            this.io.on(types_2.BroadcastTypes.FIELD_PERMISSION_SETTING_CHANGE, data => {
                this.handleFieldPermissionSetting(data);
            });
        };
        this.event = event;
        this.backupDB = localForage.createInstance({ name: VIKA_OP_BACKUP });
        if (!collaEngineMap) {
            return;
        }
        collaEngineMap.forEach(engine => {
            this.addCollaEngine(engine);
        });
        this.retryCount = Number.MAX_VALUE;
        this.justReConnected = false;
        this.isConnect();
    }
    /**
     * Create a roomId based on the resource main resource ID
     * What is a resource resource, a resource is a summary of data entities,
     * such as a datasheet, a widget, and a dashboard. Their data are collectively referred to as resources
     * The main resource ID generally refers to the node page that the current user mainly accesses, such as the datasheet, dashboard, etc.
     * The main resource may come with multiple other resources, such as an associated table of a table, a widget on a dashboard
     * @param resourceId Resource ID, which can be the ID of entity resources such as datasheetId, DashboardId, WidgetId, etc.
     */
    static createRoomId(resourceId) {
        return `${resourceId}`;
    }
    /**
     * Add an OT engine responsible for real-time collaborative collaboration on resources.
     */
    addCollaEngine(collaEngine) {
        if (collaEngine.cancelQuit) {
            collaEngine.cancelQuit();
            return;
        }
        // await collaEngine.prepare();
        if (this.collaEngineMap.has(collaEngine.resourceId)) {
            console.error('Do not add CollaEngine');
        }
        this.collaEngineMap.set(collaEngine.resourceId, collaEngine);
    }
    nextTick() {
        return __awaiter(this, void 0, void 0, function* () {
            yield new Promise(resolve => {
                setTimeout(resolve, 10);
            });
        });
    }
    init(firstRoomInit = false) {
        return __awaiter(this, void 0, void 0, function* () {
            // Ensure that the collaborative engine has been initialized
            const hasCollaEngine = () => {
                return this.collaEngineMap.has(this.roomId);
            };
            do {
                yield this.nextTick();
            } while (!hasCollaEngine());
            firstRoomInit && this.sendLocalChangesetWithInit();
            // Initialize the collaboration engine first
            yield Promise.all(Array.from(this.collaEngineMap.keys()).map(resourceId => {
                const collaEngine = this.collaEngineMap.get(resourceId);
                return collaEngine.waitPrepareComplete();
            }));
            yield this.watch();
        });
    }
    // When initializing, you need to send the locally cached localPendingChangeset (if any) to the server
    sendLocalChangesetWithInit() {
        return __awaiter(this, void 0, void 0, function* () {
            const localPendingChangesetStorage = this.lsStore.namespace(buffer_storage_1.BufferStorage.pendingChangesetsNamespace);
            const opBufferStorage = this.lsStore.namespace(buffer_storage_1.BufferStorage.bufferStorageNamespace);
            const changesetMap = localPendingChangesetStorage.getAll();
            const opBufferMap = opBufferStorage.getAll();
            if (!Object.keys(changesetMap).length && !Object.keys(opBufferMap).length) {
                return;
            }
            this.event.setRoomIOClear(false);
            opBufferStorage.clearAll();
            localPendingChangesetStorage.clearAll();
            this.clearAllStorage();
            try {
                yield this.applyLocalData(changesetMap, opBufferMap);
            }
            catch (e) {
                try {
                    yield this.backupDB.setItem(String(Date.now()), {
                        changesetMap,
                        opBufferMap
                    });
                }
                catch (e) {
                    if (e.name === 'QuotaExceededError') {
                        yield this.backupDB.clear();
                        yield this.backupDB.setItem(String(Date.now()), { changesetMap, opBufferMap });
                    }
                }
                const timestamps = yield this.backupDB.keys();
                yield Promise.all(timestamps.map((timestamp) => __awaiter(this, void 0, void 0, function* () {
                    // Clean up data whose backup time is greater than two weeks
                    if ((0, dayjs_1.default)().diff(Date.now(), 'day') > 14) {
                        yield this.backupDB.removeItem(timestamp);
                    }
                })));
                player_1.Player.doTrigger(player_1.Events.app_error_logger, {
                    error: new Error(`Failed to initialize applyChangeset: ${e.message}`),
                    metaData: {
                        roomId: this.roomId,
                        changesetMap: JSON.stringify(changesetMap),
                        opBufferMap: JSON.stringify(opBufferMap)
                    },
                });
                throw new enhance_error_1.EnhanceError({
                    code: types_1.ErrorCode.CollaModalError,
                    message: (0, i18n_1.t)(i18n_1.Strings.initialization_failed_message),
                    modalType: types_1.ModalType.Warning
                });
            }
            this.event.setRoomIOClear(true);
        });
    }
    applyLocalData(changesetMap, opBufferMap) {
        return __awaiter(this, void 0, void 0, function* () {
            if (Object.keys(changesetMap).length) {
                const res = yield api_1.Api.applyResourceChangesets(Object.values(changesetMap), this.roomId);
                const { success, message, code } = res.data;
                if (!success && code !== 4001) {
                    throw new Error(`changesetMap: ${message}`);
                }
            }
            if (Object.keys(opBufferMap).length) {
                const changesets = [];
                for (const resourceId in opBufferMap) {
                    if (changesetMap[resourceId]) {
                        const changeset = buffer_storage_1.BufferStorage.ops2Changeset(opBufferMap[resourceId], changesetMap[resourceId].baseRevision + 1, resourceId, changesetMap[resourceId].resourceType);
                        changesets.push(changeset);
                        continue;
                    }
                    const opBuffer = opBufferMap[resourceId];
                    const revision = opBuffer[0].revision;
                    const resourceType = opBuffer[0].resourceType;
                    const changeset = buffer_storage_1.BufferStorage.ops2Changeset(opBuffer, revision, resourceId, resourceType);
                    if (opBuffer.every(operation => !operation.mainLinkDstId)) {
                        const res = yield api_1.Api.applyResourceChangesets([changeset], this.roomId);
                        const { success, message } = res.data;
                        if (!success) {
                            throw new Error(`single changesets: ${message}`);
                        }
                        delete opBufferMap[resourceId];
                        continue;
                    }
                    changesets.push(changeset);
                }
                if (!changesets.length) {
                    return;
                }
                const res = yield api_1.Api.applyResourceChangesets(Object.values(changesets), this.roomId);
                const { success, message } = res.data;
                if (!success) {
                    throw new Error(`opBufferMap: ${message}`);
                }
            }
        });
    }
    /**
     * The backend server actively pushes changesets submitted by others
     * Or actively compensate for missing versions of changeset
     */
    handleNewChanges(data) {
        const state = this.store.getState();
        const user = store_1.Selectors.getUserState(state);
        const userInfo = user.info;
        data.changesets.forEach(cs => {
            console.log('------handleNewChanges---------', cs.userId, userInfo);
            if (cs.uuid != (userInfo === null || userInfo === void 0 ? void 0 : userInfo.uuid)) { //网络断了后，自己会发过来造成混乱，一个账号多地登录可能有问题。
                const resourceId = cs.resourceId;
                const collaEngine = this.collaEngineMap.get(resourceId);
                if (collaEngine) {
                    void collaEngine.handleNewChanges(cs);
                }
                else if (resourceId.startsWith(constant_1.NodeTypeReg.DATASHEET) &&
                    !store_1.Selectors.getDatasheet(this.store.getState(), resourceId)) {
                    // The data obtained at this time is the latest version, no need to apply cs anymore
                    void this.fetchResource(resourceId, types_1.ResourceType.Datasheet);
                }
            }
        });
    }
    /**
     * The @description watch method is mainly used to do 3 things
     * 1. Create a room on the client side and add it to the service
     * 2. Register listening events for various sockets
     * 3. Start the event coordination monitor on the client to ensure that blocked messages will be processed at intervals
     * Originally considered to compensate for the missing version in watch,
     * but the compensation behavior is already in init, handled by each engine itself,
     * so watch does not need to pay attention to version compensation anymore
     * @returns
     */
    watch() {
        return __awaiter(this, void 0, void 0, function* () {
            const state = this.store.getState();
            const shareId = state.pageParams.shareId;
            const embedId = state.pageParams.embedId;
            const watchResponse = yield this.io.watch(this.roomId, shareId, embedId).catch(e => {
                throw new enhance_error_1.EnhanceError(e);
            });
            this.setConnected(true);
            if (!watchResponse) {
                return;
            }
            const { resourceRevisions, collaborators } = watchResponse.data;
            console.log('resourceRevisions:', resourceRevisions);
            const collaEngine = this.collaEngineMap.get(this.roomId);
            if (!collaEngine) {
                return;
            }
            yield this.checkVersion(resourceRevisions);
            const resourceType = collaEngine.resourceType;
            this.store.dispatch((0, actions_1.roomInfoSync)(this.roomId, resourceType, collaborators || []));
            this.store.dispatch((0, actions_1.setResourceConnect)(this.roomId, resourceType));
            this.loadFieldPermissionMap();
            this.bindSocketMessage();
            this.setSendingWatcher();
        });
    }
    /**
     * Switching resources or reconnecting after disconnection requires the column permissions of all data tables in the room
     */
    loadFieldPermissionMap() {
        const dstIds = [];
        this.collaEngineMap.forEach(collaEngine => {
            if (collaEngine.resourceType !== types_1.ResourceType.Datasheet) {
                return;
            }
            dstIds.push(collaEngine.resourceId);
        });
        dstIds.length && this.store.dispatch(store_1.StoreActions.fetchFieldPermission(dstIds));
    }
    checkVersion(resourceRevisions) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const missVersionEngines = [];
            if (!resourceRevisions || !resourceRevisions.length) {
                console.log('The current resource has not been operated for a long time, and the data is automatically updated');
                yield ((_a = this.collaEngineMap.get(this.roomId)) === null || _a === void 0 ? void 0 : _a.event.reloadResourceData());
                return;
            }
            for (const { resourceId, revision } of resourceRevisions) {
                const collaEngine = this.collaEngineMap.get(resourceId);
                if (!collaEngine) {
                    continue;
                }
                const baseRevision = collaEngine.getRevision();
                const versionDiffs = (0, utils_1.numbersBetween)(baseRevision, revision + 1);
                if (!versionDiffs.length) {
                    continue;
                }
                if (versionDiffs.length >= 100) {
                    throw new enhance_error_1.EnhanceError({
                        code: config_1.StatusCode.FRONT_VERSION_ERROR,
                        message: (0, i18n_1.t)(i18n_1.Strings.changeset_diff_big_tip),
                        modalType: types_1.ModalType.Info
                    });
                }
                missVersionEngines.push({ collaEngine, revision });
            }
            return yield Promise.all(missVersionEngines.map(({ collaEngine, revision }) => {
                return collaEngine.prepare(revision + 1);
            }));
        });
    }
    /**
     * @description unwatch no longer needs to pay attention to whether the coordinator queue is emptied,
     * there are two cases here: switch table and complete disconnection of ƒ socket.
     * The processing of the latter needs to rely on locally stored data, and the unwatch event here will not be triggered.
     * For the former, the service has already converted the engine data in the process of switching rooms,
     * which ensures that even if there is unsent data in the previous room, it can be coordinated after switching rooms.
     * So here you only need to pay attention to the notification that the middle server(room) leaves the room
     * @private
     */
    unwatch() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.io.unWatch();
        });
    }
    /**
     * leave room
     * 1. Remove long chain message monitoring
     * 2. Clear data sending status monitoring timer
     */
    leaveRoom() {
        return __awaiter(this, void 0, void 0, function* () {
            this.setConnected(false);
            /**
             * After switching the space, the socket of the previous space is closed, so the event of leave_room will not be triggered, so
             * You can check the connection status of the item socket here
             */
            if (this.socket.connected) {
                yield this.unwatch();
            }
            else {
                console.log('socket has been closed, room needn\'t leave again');
            }
            this.clearSendingWatcher();
            return this.collaEngineMap;
        });
    }
    /**
     * @description supports a single Resource to exit the current collaborative Room without destroying the current Room
     * @param {string} resourceId
     * @returns
     */
    quit(resourceId) {
        var _a;
        const collaEngine = this.collaEngineMap.get(resourceId);
        if (!collaEngine) {
            return;
        }
        if (collaEngine.isStorageClear()) {
            this.collaEngineMap.delete(resourceId);
            (_a = collaEngine.cancelQuit) === null || _a === void 0 ? void 0 : _a.call(collaEngine);
            this.store.dispatch(store_1.StoreActions.resetResource(resourceId, collaEngine.resourceType));
            return;
        }
        if (collaEngine.cancelQuit) {
            return;
        }
        const cancelId = setInterval(() => {
            this.quit(resourceId);
        }, 100);
        collaEngine.cancelQuit = () => {
            clearInterval(cancelId);
        };
    }
    /**
      * Data sending status listener
      * Regular polling to check whether user data has been sent and confirmed
      * If it has not been confirmed for a long time (1 minute), execute a retry mechanism
      * This situation usually occurs with an error
     */
    setSendingWatcher() {
        this.clearSendingWatcher();
        this.sendingWatcherTimer = setInterval(() => {
            if (!this.isConnect()) {
                return;
            }
            const changesets = [];
            for (const key of this.collaEngineMap.keys()) {
                const localChangeset = this.collaEngineMap.get(key).bufferStorage.localPendingChangeset;
                if (localChangeset) {
                    changesets.push(localChangeset);
                }
            }
            if (changesets.length > 0) {
                if (this.retryCount < MAX_RETRY_COUNT) {
                    this.retryCount++;
                    this.forceSend(changesets, config_1.StatusCode.SAVE_ERROR_RUNING);
                }
                else 
                //网络异常,刚修复好了，原来的请求不会有结果了，要马上发送请求，不要再等了
                if (this.justReConnected) {
                    this.justReConnected = false;
                    this.forceSend(changesets, config_1.StatusCode.SAVE_RECONNECT_RUNING);
                }
                else if (Date.now() - this.event.getRoomLastSendTime() > 60 * 1000) {
                    this.forceSend(changesets, config_1.StatusCode.SAVE_TIMEOUT_RUNING);
                }
            }
        }, 10 * 1000);
    }
    clearSendingWatcher() {
        clearInterval(this.sendingWatcherTimer);
    }
    // Force data to be sent to the server to avoid data jamming and not sending due to process problems.
    // As long as a persistent connection exists, data will be sent.
    forceSend(changesets, runStatus) {
        console.log('forceSend' + runStatus);
        const actionLength = changesets.reduce((pre, cur) => {
            return pre + cur.operations.reduce((p, c) => {
                return p + c.actions.length;
            }, 0);
        }, 0);
        this.justReConnected = false;
        // Note: In the case of pasting large data, the server may return slowly, and the data will not be re-sent at this time.
        if (actionLength > MAX_RETRY_LENGTH) {
            console.error('Paste data in large batches, the retry operation will not be performed');
            return;
        }
        player_1.Player.doTrigger(player_1.Events.app_error_logger, {
            error: new Error('force send userChanges'),
        });
        this.event.setRoomLastSendTime();
        void this.sendUserChanges(changesets, runStatus);
    }
    handleAcceptCommit(changesets) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const cs of changesets) {
                const collaEngine = this.collaEngineMap.get(cs.resourceId);
                if (!collaEngine) {
                    console.log('The room has been switched, the collaEngine is updated');
                    continue;
                }
                yield collaEngine.handleAcceptCommit(cs);
                // TODO: There are cookies in changesets to be removed in the middle layer
                console.log('Data returned successfully: ', cs);
            }
            this.nextSend();
        });
    }
    handleRejectCommit(data, clearAllStorage = true) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const bufferStorage = (_a = this.collaEngineMap.get(this.roomId)) === null || _a === void 0 ? void 0 : _a.bufferStorage;
            player_1.Player.doTrigger(player_1.Events.app_error_logger, {
                error: new Error(`Incorrect op message: ${data.message}`),
                metaData: {
                    roomId: this.roomId,
                    connected: this.connected,
                    socketConnected: this.io.socket.connected,
                    opBuffer: JSON.stringify(bufferStorage === null || bufferStorage === void 0 ? void 0 : bufferStorage.opBuffer),
                    localPendingChangeset: JSON.stringify(bufferStorage === null || bufferStorage === void 0 ? void 0 : bufferStorage.localPendingChangeset),
                },
            });
            if (clearAllStorage) {
                // Before clearing the data, make a local backup of the cleared data
                yield this.backupDB.setItem(String(Date.now()), {
                    error: data,
                    opBufferMap: this.getBufferOperateMap(),
                    changesetMap: (0, lodash_1.keyBy)(this.getLocalPendingChangesets(), 'resourceId')
                });
                //this.clearAllStorage();//这里有问题吧，可以把LocalPendingChangesets清掉，buffer不能清的，因为有可能用户有新的修改，已经push到 buffer了
                this.clearAllPendingSet();
                this.nextSend(); //开始发送重试期间用户操作数据
                data.message = '保存数据错误，请刷新后，重新提交';
                //throw new EnhanceError(data);
            }
            else {
                data.message = '保存数据错误，15秒后，准备重试';
                //throw new EnhanceError(data);
            }
            console.trace('Wrong op information retryCount ' + this.retryCount);
        });
    }
    getBufferOperateMap() {
        const bufferOperateMap = {};
        for (const resourceId of this.collaEngineMap.keys()) {
            const collaEngine = this.collaEngineMap.get(resourceId);
            const operate = collaEngine.getOpBuffer();
            if (operate) {
                bufferOperateMap[resourceId] = operate;
            }
        }
        return bufferOperateMap;
    }
    getLocalPendingChangesets() {
        const localPendingChangeset = [];
        for (const resourceId of this.collaEngineMap.keys()) {
            const collaEngine = this.collaEngineMap.get(resourceId);
            const cs = collaEngine.getLocalPendingChangeset();
            if (cs) {
                localPendingChangeset.push(cs);
            }
        }
        return localPendingChangeset;
    }
    // Clear all pending ops and pending changesets
    clearAllStorage() {
        for (const resourceId of this.collaEngineMap.keys()) {
            const collaEngine = this.collaEngineMap.get(resourceId);
            collaEngine.clearLocalPendingChangeset();
            collaEngine.clearOpBuffer();
        }
    }
    // Clear all pending changesets
    clearAllPendingSet() {
        for (const resourceId of this.collaEngineMap.keys()) {
            const collaEngine = this.collaEngineMap.get(resourceId);
            collaEngine.clearLocalPendingChangeset();
        }
    }
    /**
     * Push the operation into the send queue, SyncEngine will ensure that the data is sent to the server in version order
     * And provide temporary local persistence capabilities to
     * prevent users from losing data in the case of accidental network disconnection and active refresh
     *
     * Notice: Multiple operations may be merged into one changeset and sent to the server at one time
     *
     */
    syncOperations(localOperations) {
        localOperations.forEach(lop => {
            const collaEngine = this.collaEngineMap.get(lop.resourceId);
            lop.operations.forEach(op => collaEngine === null || collaEngine === void 0 ? void 0 : collaEngine.pushOpBuffer(op));
        });
        this.nextSend();
    }
    /**
     * Is it safe to close?
     * When there is still data that has not been successfully synchronized, return false, otherwise return true
     */
    isSafeToClose() {
        for (const resourceId of this.collaEngineMap.keys()) {
            const collaEngine = this.collaEngineMap.get(resourceId);
            if (!collaEngine.isStorageClear()) {
                return false;
            }
        }
        return true;
    }
    /**
     * A simple message sender for situations where only notifications need to be sent and no persistence is required.
     * message will be sent to all online
     */
    sendMessages(type, data) {
        return this.io.request(Object.assign({ type }, data));
    }
    getCollaEngine() {
        return this.collaEngineMap.get(this.roomId);
    }
    /**
     * Activate the collaborator. After calling this method,
     * the avatar of the current user will be displayed on the interface of all collaborators who open this table
     */
    handleActiveCollaborators(data) {
        console.log('active collaborators', data);
        const collaEngine = this.getCollaEngine();
        if (!collaEngine) {
            return;
        }
        data.collaborators.forEach(item => {
            this.store.dispatch(store_1.StoreActions.activeCollaborator(item, this.roomId, collaEngine.resourceType));
        });
    }
    /**
     * Deactivate the collaborator.
     * After calling this method, the avatar of the current user will be left on the interface of all collaborators who open this table
     */
    handleDeactivateCollaborator(data) {
        console.log('deactivate collaborator', data);
        const collaEngine = this.getCollaEngine();
        if (!collaEngine) {
            return;
        }
        this.store.dispatch(store_1.StoreActions.deactivateCollaborator(data, this.roomId, collaEngine.resourceType));
    }
    handleCursor(data) {
        console.log('RECEIVED IEngagementCursorData: ', { data });
        const { cursorInfo } = data;
        this.store.dispatch(store_1.StoreActions.cursorMove({
            fieldId: cursorInfo.fieldId,
            recordId: cursorInfo.recordId,
            time: cursorInfo.time,
            socketId: data.socketId,
        }, this.roomId));
    }
    handleNodeShareDisabled(data) {
        var _a, _b;
        console.log('RECEIVED INodeShareDisabledData: ', { data });
        const state = this.store.getState();
        const shareId = state.pageParams.shareId;
        if (shareId && data.shareIds.includes(shareId)) {
            (_b = (_a = this.event).destroy) === null || _b === void 0 ? void 0 : _b.call(_a);
            throw new enhance_error_1.EnhanceError({
                code: config_1.StatusCode.NODE_NOT_EXIST,
                message: (0, i18n_1.t)(i18n_1.Strings.error_please_close_sharing_page),
                okText: (0, i18n_1.t)(i18n_1.Strings.okay),
                modalType: types_1.ModalType.Warning,
                onOkType: types_1.OnOkType.BackWorkBench
            });
        }
    }
    generateStdFieldPermission(data, manageable = false) {
        return {
            [data.fieldId]: {
                role: data.role || config_1.ConfigConstant.Role.None,
                setting: data.setting || {
                    formSheetAccessible: false,
                },
                permission: data.permission || store_1.DEFAULT_FIELD_PERMISSION,
                manageable,
            },
        };
    }
    // Enable column permissions
    handleFieldPermissionEnabled(data) {
        console.log('RECEIVED handleFieldPermissionEnabled: ', { data });
        const fieldPermission = this.generateStdFieldPermission(data, true);
        this.store.dispatch((0, actions_1.updateFieldPermissionMap)(fieldPermission, data.datasheetId));
    }
    // change column permissions
    handleFieldPermissionChange(data) {
        console.log('RECEIVED handleFieldPermissionChange: ', { data });
        const fieldPermission = this.generateStdFieldPermission(data);
        this.store.dispatch((0, actions_1.updateFieldPermissionMap)(fieldPermission, data.datasheetId));
    }
    // disable column permissions
    handleFieldPermissionDisabled(data) {
        console.log('RECEIVED handleFieldPermissionDisabled: ', { data });
        this.store.dispatch((0, actions_1.resetFieldPermissionMap)(data.fieldId, data.datasheetId));
    }
    // Modify the configuration of column permissions
    // 0.7 don't handle the event
    handleFieldPermissionSetting(data) {
        console.log('RECEIVED IFieldPermissionMessage: ', { data });
        if (!data.setting) {
            return;
        }
        this.store.dispatch((0, actions_1.updateFieldPermissionSetting)(data.fieldId, data.setting, data.datasheetId));
    }
    setConnected(status) {
        if (!this.connected && status) {
            this.justReConnected = true;
            this.retryCount = Number.MAX_VALUE; //重试的过程网络断了，那么重新开始
        }
        this.connected = status;
    }
    getStatus() {
        return this.event.getRoomIOClear();
    }
}
__decorate([
    (0, utils_1.errorCapture)(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], RoomService.prototype, "sendLocalChangesetWithInit", null);
__decorate([
    (0, utils_1.errorCapture)(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], RoomService.prototype, "handleNewChanges", null);
__decorate([
    (0, utils_1.errorCapture)(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], RoomService.prototype, "watch", null);
__decorate([
    (0, utils_1.errorCapture)(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], RoomService.prototype, "checkVersion", null);
__decorate([
    (0, utils_1.errorCapture)(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", Promise)
], RoomService.prototype, "handleAcceptCommit", null);
__decorate([
    (0, utils_1.errorCapture)(),
    (0, socket_guard_1.socketGuard)(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Object]),
    __metadata("design:returntype", Promise)
], RoomService.prototype, "handleRejectCommit", null);
__decorate([
    (0, utils_1.errorCapture)(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], RoomService.prototype, "syncOperations", null);
__decorate([
    (0, utils_1.errorCapture)(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], RoomService.prototype, "handleNodeShareDisabled", null);
exports.RoomService = RoomService;
//# sourceMappingURL=room.js.map